import clsx from "clsx";
import React, {
  FC,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
  Account,
  Asset,
  Balance,
  BalanceAccount,
  MsgSecureWithdraw,
  MsgSwap,
  Network,
  THOR,
  TxResult,
  networkLabel,
  validateAddress,
} from "rujira.js";
import {
  AddressValidationError,
  Decimal,
  CustomSelect,
  Fiat,
  IconDenom,
  LoaderWithContent,
  NetworkIcon,
  Provider,
  SimulationState,
  TransactionFeeSummary,
  TransferTimeValue,
  useTranslation,
} from "rujira.ui";
import { usePreloadedBalance } from "../../../common/components/Balance";
import {
  Destination,
  DestinationOption,
} from "../../../common/components/Destination";
import {
  TransactionAddress,
  TransactionDetailRow,
  TransactionDetails,
  TransferAddressWarning,
  TransferAccountOption,
  TransferAccountSelector,
  TransferAmountField,
  TransferFooter,
  TransferForm,
  TransferModalHeader,
  transferSimulationFees,
} from "../../../common/components/TransferModal";
import { MsgProvider, TxButton } from "../../../common/components/TxButton";
import { buildWithdrawalFeeSummary } from "../../../common/components/WithdrawalSummary";
import { TransferSuccess } from "../../../common/components/TransferSuccess";
import { useAccounts } from "../../../services/accounts";
import { isAmountRepresentable } from "../../../services/amountPrecision";
import { isSameCanonicalAsset } from "../../../services/canonicalAsset";
import {
  isSameTransferAsset,
  withdrawAmountIssue,
} from "../../../services/transferAsset";
import { Quote, useQuote } from "../../../services/useQuote";
import { WithdrawQuery } from "./__generated__/WithdrawQuery.graphql";

const query = graphql`
  query WithdrawQuery {
    thorchainV2 {
      outboundFees {
        asset {
          asset
          type
          chain
          price {
            current
          }
          metadata {
            symbol
            decimals
          }
          variants {
            secured {
              chain
              type
              asset
              metadata {
                symbol
                decimals
              }
            }
          }
        }
        outboundFee
      }
      rune {
        price {
          current
        }
        metadata {
          decimals
        }
      }
      pools {
        status
        asset {
          asset
          type
          chain
          price {
            current
          }
          metadata {
            symbol
            decimals
          }
          variants {
            secured {
              chain
              type
              asset
              variants {
                native {
                  denom
                }
              }
              metadata {
                symbol
                decimals
              }
            }
          }
        }
        assetTorPrice
      }
    }
  }
`;

export const Withdraw: React.FC<{
  cancel: () => void;
  connect?: () => void;
}> = ({ cancel, connect = () => undefined }) => {
  const { t } = useTranslation("common");
  const [amount, setAmount] = useState(0n);
  const [destination, setDestination] = useState<Destination>();
  const [simulationState, setSimulationState] = useState<SimulationState>();
  const [result, setResult] = useState<{
    tx: TxResult;
    asset: Asset;
    arrivalNetwork: Network;
  }>();
  const { balance, account, asset } = usePreloadedBalance();
  const shouldSwap = Boolean(
    destination &&
      account &&
      !isSameTransferAsset(destination.asset, account.asset)
  );
  const validAmount = Boolean(
    account &&
      amount > 0n &&
      amount <= account.balance &&
      isAmountRepresentable(amount, account.asset.metadata.decimals)
  );
  const request = useMemo(() => {
    if (
      !shouldSwap ||
      !validAmount ||
      !account?.asset ||
      !destination?.address ||
      !validateAddress(destination.asset.chain, destination.address)
    )
      return null;
    return {
      from: account.asset.asset,
      to: destination.asset.asset,
      amount,
      destination: destination.address,
    };
  }, [account, amount, destination, shouldSwap, validAmount]);
  const quote = useQuote(request);
  const swapFee = shouldSwap
    ? (typeof quote === "object" && "memo" in quote && quote.fees?.total) || 0n
    : 0n;
  const changeAmount = useCallback((value: bigint) => {
    setAmount(value);
    setSimulationState(undefined);
  }, []);
  const changeDestination = useCallback((value: Destination) => {
    setDestination(value);
    setSimulationState(undefined);
  }, []);

  if (result) {
    return (
      <TransferSuccess
        action="withdraw"
        asset={result.asset}
        tx={result.tx}
        dismiss={cancel}
        arrivalNetwork={result.arrivalNetwork}
      />
    );
  }
  if (!balance || !account) return null;
  return (
    <div className="transfer-modal">
      <TransferModalHeader
        action={t("withdraw")}
        symbol={asset.metadata.symbol}
        suffix={t("fromRujira")}
        hideSuffixOnMobile
        description={t("withdrawTransactionDescription")}
      />
      <Suspense>
        <WithdrawContent
          asset={asset}
          account={account}
          balance={balance}
          amount={amount}
          setAmount={changeAmount}
          destination={destination}
          setDestination={changeDestination}
          swapFee={swapFee}
          shouldSwap={shouldSwap}
          quote={quote}
          simulationState={simulationState}
          setSimulationState={setSimulationState}
          onSuccess={(tx, destinationAsset) =>
            setResult({
              tx,
              asset: destinationAsset,
              arrivalNetwork: destinationAsset.chain,
            })
          }
          cancel={cancel}
          connect={connect}
        />
      </Suspense>
    </div>
  );
};

const WithdrawContent: FC<{
  asset: Asset;
  account: BalanceAccount;
  amount: bigint;
  setAmount: (value: bigint) => void;
  balance: Balance;
  destination?: Destination;
  setDestination: (value: Destination) => void;
  swapFee: bigint;
  shouldSwap: boolean;
  quote: Quote;
  simulationState?: SimulationState;
  setSimulationState: (value: SimulationState | undefined) => void;
  onSuccess: (tx: TxResult, destinationAsset: Asset) => void;
  cancel: () => void;
  connect: () => void;
}> = ({
  asset,
  account,
  amount,
  setAmount,
  balance,
  destination,
  setDestination,
  swapFee,
  shouldSwap,
  quote,
  simulationState,
  setSimulationState,
  onSuccess,
  cancel,
  connect,
}) => {
  const { t } = useTranslation("portfolio");
  const data = useLazyLoadQuery<WithdrawQuery>(query, {});
  const { accounts } = useAccounts();
  const eligibleAssets = useMemo(
    () =>
      (data.thorchainV2?.pools || [])
        .filter(
          (pool) =>
            pool.status === "AVAILABLE" &&
            pool.asset.chain !== THOR &&
            isSameCanonicalAsset(
              pool.asset.metadata.symbol,
              balance.asset.metadata.symbol
            )
        )
        .map((pool) => pool.asset),
    [balance.asset.metadata.symbol, data.thorchainV2?.pools]
  );
  const options = useMemo(
    () =>
      eligibleAssets.flatMap((item) => collectOptions(accounts || [])(item)),
    [accounts, eligibleAssets]
  );
  const accountOptions = useMemo<TransferAccountOption[]>(
    () =>
      options.map((item) => ({
        id: `${item.asset.asset}:${item.address}`,
        address: item.address,
        network: item.asset.chain,
        providers: item.providers,
      })),
    [options]
  );
  const selectedId = destination
    ? `${destination.asset.asset}:${destination.address}`
    : undefined;
  const hasSelectedConnected = options.some(
    (item) =>
      item.asset.asset === destination?.asset.asset &&
      item.address === destination.address
  );
  const [customRecipient, setCustomRecipient] = useState(options.length === 0);
  const [explicitCustomRecipient, setExplicitCustomRecipient] = useState(false);
  const [customNetwork, setCustomNetwork] = useState(
    eligibleAssets[0]?.asset || ""
  );
  const [customAddress, setCustomAddress] = useState("");
  const customAsset =
    eligibleAssets.find((item) => item.asset === customNetwork) ||
    eligibleAssets[0];
  const shouldAdoptConnectedOption = Boolean(
    options[0] &&
      !explicitCustomRecipient &&
      customRecipient &&
      !customAddress.trim() &&
      !destination?.address
  );

  useEffect(() => {
    if (customRecipient || !destination || hasSelectedConnected) return;
    const first = options[0];
    if (first) {
      setDestination({ address: first.address, asset: first.asset });
    } else {
      setCustomRecipient(true);
      setDestination({
        address: "",
        asset: customAsset || destination.asset,
      });
    }
    setSimulationState(undefined);
  }, [
    customAsset,
    customRecipient,
    destination,
    hasSelectedConnected,
    options,
    setDestination,
    setSimulationState,
  ]);

  useEffect(() => {
    if (!options[0] || (destination && !shouldAdoptConnectedOption)) return;
    if (shouldAdoptConnectedOption) setCustomRecipient(false);
    setDestination({ address: options[0].address, asset: options[0].asset });
  }, [destination, options, setDestination, shouldAdoptConnectedOption]);

  useEffect(() => {
    if (!customRecipient || !customAsset || shouldAdoptConnectedOption) return;
    const valid = validateAddress(customAsset.chain, customAddress.trim());
    setDestination({
      address: valid ? customAddress.trim() : "",
      asset: customAsset,
    });
  }, [
    customAddress,
    customAsset,
    customRecipient,
    setDestination,
    shouldAdoptConnectedOption,
  ]);

  const outboundFee = data.thorchainV2?.outboundFees.find(
    (item) => item.asset.variants.secured?.asset === balance.asset.asset
  );
  const outboundFeeAmount = BigInt(outboundFee?.outboundFee || 0);
  const networkFees = transferSimulationFees(simulationState);
  const networkFeeItems = networkFees.map((networkFee) => ({
    symbol: networkFee.symbol,
    amount: networkFee.amount,
    decimals: networkFee.decimals,
    valueUsd: data.thorchainV2?.rune?.price?.current
      ? (networkFee.amount * data.thorchainV2.rune.price.current) / 10n ** 12n
      : 0n,
  }));
  const networkFeeValue = networkFeeItems.reduce(
    (total, item) => total + item.valueUsd,
    0n
  );
  const fee = shouldSwap ? swapFee : outboundFeeAmount;
  const feeAsset = shouldSwap ? destination?.asset : outboundFee?.asset;
  const withdrawalFeeValue =
    ((feeAsset?.price?.current || 0n) * fee) / 10n ** 12n;
  const received = shouldSwap
    ? typeof quote === "object" && "memo" in quote
      ? quote.expectedAmountOut
      : 0n
    : fee > amount
      ? 0n
      : amount - fee;
  const { fees, totalValueUsd } = buildWithdrawalFeeSummary({
    networkFees: networkFeeItems,
    networkFeeValueUsd: networkFeeValue,
    withdrawalFee: feeAsset
      ? {
          symbol: feeAsset.metadata.symbol,
          amount: fee,
          decimals: 8,
        }
      : undefined,
    withdrawalFeeValueUsd: withdrawalFeeValue,
    networkFeeLabel: t("common:networkFee"),
    withdrawalFeeLabel: t("common:withdrawalFee"),
  });
  const receivedValue =
    ((feeAsset?.price?.current || 0n) * received) / 10n ** 12n;
  const canSimulate = amount > 0n && Boolean(destination?.address);
  const feeReady = canSimulate && simulationState?.status === "completed";
  const feeFailed =
    simulationState?.status === "failed" || quote instanceof Error;
  const feePending = canSimulate && !feeReady && !feeFailed;
  const showWithdrawalFee = feeReady || (feeFailed && Boolean(outboundFee));
  const visibleFees = feeFailed
    ? fees.filter((item) => item.key === "withdrawal")
    : fees;
  const visibleFeeValue = feeFailed ? withdrawalFeeValue : totalValueUsd;
  const customInvalid =
    customRecipient &&
    Boolean(customAddress.trim()) &&
    Boolean(customAsset) &&
    !validateAddress(customAsset!.chain, customAddress.trim());
  const receivedKnown =
    !shouldSwap || (typeof quote === "object" && "memo" in quote);
  const amountIssue = withdrawAmountIssue({
    amount,
    balance: account.balance,
    received: receivedKnown ? received : undefined,
  });
  const amountHasUnsupportedPrecision = !isAmountRepresentable(
    amount,
    asset.metadata.decimals
  );
  const amountError =
    amountIssue === "exceedsBalance"
      ? t("common:amountExceedsBalance")
      : amountIssue === "belowFee"
        ? t("common:amountBelowWithdrawalFee")
        : amountHasUnsupportedPrecision
          ? t("common:unsupportedAmountPrecision", {
              decimals: Math.min(asset.metadata.decimals, 8),
            })
          : undefined;

  return (
    <TransferForm
      amount={
        <TransferAmountField
          label={t("common:amount")}
          className="bg-black"
          symbol={asset.metadata.symbol}
          amount={amount}
          decimals={8}
          onChangeAmount={setAmount}
          max={account.balance}
          maxLabel={`${t("common:balance")}:`}
          fiat={{ price: asset.price?.current, symbol: "$" }}
          invalid={Boolean(amountError)}
          errorTooltip={amountError}
        />
      }
      destination={
        <>
          <TransferAccountSelector
            label={t("common:withdrawTo")}
            name="withdraw-destination"
            options={accountOptions}
            value={
              !customRecipient && hasSelectedConnected ? selectedId : undefined
            }
            onChange={(option) => {
              const selected = options.find(
                (item) => `${item.asset.asset}:${item.address}` === option.id
              );
              if (selected) {
                setExplicitCustomRecipient(false);
                setCustomRecipient(false);
                setDestination({
                  address: selected.address,
                  asset: selected.asset,
                });
              }
            }}
            onConnect={connect}
            showAddressActions
            custom={{
              selected: customRecipient,
              value: "",
              onSelect: () => {
                setExplicitCustomRecipient(true);
                setCustomRecipient(true);
              },
              onChange: () => undefined,
              placeholder: "",
              inline: false,
            }}
          />
          {customRecipient && (
            <div className="transfer-modal__custom-withdraw mt-1">
              <label className="color-grey fw-500">{t("common:network")}</label>
              <CustomSelect
                className="custom-select--sm"
                value={customAsset?.asset || ""}
                onChange={setCustomNetwork}
                options={eligibleAssets.map((item) => ({
                  key: item.asset,
                  label: (
                    <>
                      <NetworkIcon network={item.chain} />
                      <span>{networkLabel(item.chain)}</span>
                    </>
                  ),
                }))}
                ariaLabel={t("common:network")}
              />
              <label className="color-grey fw-500">{t("common:address")}</label>
              <input
                autoFocus
                aria-invalid={customInvalid || undefined}
                className={clsx(
                  "transfer-modal__withdraw-address",
                  customInvalid && "transfer-modal__withdraw-address--error"
                )}
                value={customAddress}
                onChange={(event) =>
                  setCustomAddress(event.currentTarget.value)
                }
                placeholder={t("common:enterNetworkAddress", {
                  network: customAsset ? networkLabel(customAsset.chain) : "",
                })}
              />
            </div>
          )}
          {customRecipient && customInvalid && customAsset && (
            <AddressValidationError network={networkLabel(customAsset.chain)} />
          )}
        </>
      }
      warning={
        customRecipient ? (
          <TransferAddressWarning>
            {t("common:withdrawCustomAddressWarning")}
          </TransferAddressWarning>
        ) : undefined
      }
      details={
        <TransactionDetails
          title={t("common:transactionDetails")}
          titleClassName={customRecipient ? "mt-2" : undefined}>
          <TransactionDetailRow label={t("common:asset")}>
            {destination?.asset ? (
              <span className="flex ai-c jc-e fw-500">
                <IconDenom
                  denom={destination.asset.metadata.symbol}
                  className="block w-2 h-2 mr-0.5"
                />
                {destination.asset.metadata.symbol}
              </span>
            ) : (
              <span className="color-grey">&mdash;</span>
            )}
          </TransactionDetailRow>
          <TransactionDetailRow
            label={t("common:withdrawalFee")}
            tooltip={t("common:feesTooltip")}>
            <LoaderWithContent
              loading={feePending}
              content={
                showWithdrawalFee ? (
                  <TransactionFeeSummary
                    fees={visibleFees}
                    totalValueUsd={visibleFeeValue}
                  />
                ) : (
                  <span className="color-grey">&mdash;</span>
                )
              }
            />
          </TransactionDetailRow>
          <TransactionDetailRow
            label={t("common:withdrawalTime")}
            tooltip={t("common:withdrawalTimeTooltip")}>
            <TransferTimeValue network={destination?.asset.chain} />
          </TransactionDetailRow>
          <TransactionDetailRow
            label={t("totalReceived")}
            tooltip={t("common:withdrawReceivedAmountTooltip")}>
            <LoaderWithContent
              loading={feePending}
              content={
                showWithdrawalFee ? (
                  <>
                    <Decimal
                      amount={received}
                      symbol={feeAsset?.metadata.symbol}
                      clip
                      symbolClassName="color-grey condensed fw-500"
                    />
                    <span className="color-grey ml-1">
                      (
                      <Fiat
                        amount={receivedValue}
                        symbol="$"
                        decimals={8}
                        clip
                      />
                      )
                    </span>
                  </>
                ) : (
                  <span className="color-grey">&mdash;</span>
                )
              }
            />
          </TransactionDetailRow>
          <TransactionDetailRow label={t("common:receivingAddress")}>
            <TransactionAddress
              address={destination?.address}
              network={destination?.asset.chain}
            />
          </TransactionDetailRow>
        </TransactionDetails>
      }
      footer={
        <TransferFooter
          cancel={cancel}
          action={
            <Confirm
              amount={amount}
              destination={destination}
              account={account}
              shouldSwap={shouldSwap}
              quote={quote}
              onSimulation={setSimulationState}
              onSuccess={(tx) => {
                if (!destination) return;
                onSuccess(tx, destination.asset);
              }}
              disabled={customInvalid}
              amountInvalid={Boolean(amountError)}
            />
          }
        />
      }
    />
  );
};

const Confirm: FC<{
  amount: bigint;
  account?: BalanceAccount;
  destination?: Destination;
  shouldSwap: boolean;
  quote: Quote;
  onSimulation: (state: SimulationState) => void;
  onSuccess: (tx: TxResult) => void;
  disabled?: boolean;
  amountInvalid?: boolean;
}> = ({
  account,
  destination,
  amount,
  shouldSwap,
  quote,
  onSimulation,
  onSuccess,
  disabled,
  amountInvalid,
}) => {
  const { t } = useTranslation("portfolio");
  const { selected } = useAccounts();
  const msg = useMemo(() => {
    if (
      !amount ||
      !account ||
      amount > account.balance ||
      !selected ||
      !destination?.address ||
      disabled ||
      amountInvalid
    )
      return null;
    return shouldSwap
      ? typeof quote === "object" && "memo" in quote
        ? new MsgSwap(
            Account.fromAddress(selected.address),
            account.asset,
            amount,
            quote.memo
          )
        : null
      : new MsgSecureWithdraw(
          Account.fromAddress(selected.address),
          account.asset,
          amount,
          destination.address
        );
  }, [
    account,
    amount,
    amountInvalid,
    destination,
    disabled,
    quote,
    selected,
    shouldSwap,
  ]);

  return (
    <MsgProvider msg={msg}>
      <TxButton
        className="button ml-1"
        label={t("common:confirm")}
        onSuccess={onSuccess}
        onSimulation={onSimulation}
        assetSymbol={account?.asset.metadata.symbol}
        hideSimulation
        disabled={disabled || amountInvalid}
      />
    </MsgProvider>
  );
};

const collectOptions =
  (accounts: { address: { address: string }; provider: Provider.Key }[]) =>
  (asset: Asset): DestinationOption[] => {
    const byAddress = new Map<string, Provider.Key[]>();
    for (const item of accounts) {
      if (!validateAddress(asset.chain, item.address.address)) continue;
      const providers = byAddress.get(item.address.address) || [];
      if (!providers.includes(item.provider)) providers.push(item.provider);
      byAddress.set(item.address.address, providers);
    }
    return [...byAddress.entries()].map(([address, providers]) => ({
      asset,
      address,
      network: asset.chain,
      providers,
    }));
  };
